import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { withApollo } from 'react-apollo';
import { withToastManager } from 'react-toast-notifications';

import {
  CREATE_PAYMENT_INTENT,
  CURRENT_USER,
  GET_ASYNC_TEMPORARY_RESPONSES,
  UPCOMING_APPOINTMENTS,
  GET_MEMBERSHIPS,
  APPOINTMENTS,
  CREATE_PAYMENT_INTENT_BRIDGE,
  APPOINTMENTS_HISTORY,
  GET_BRIDGE_CONSENTS_TO_AGREE,
  GET_STRIPE_DEFAULT_PAYMENT_METHOD_CARD,
} from '../../graphql';
import { Box, Button, Card, Flex, Heading, Text } from 'rebass';
import withSession from '../../lib/withSession';
import PleaseWaitRotatingText from '../../components/PleaseWaitRotatingText';
import UseCurrentCard, { UseCurrentDefaultPaymentCard } from '../asyncIntake/screens/components/checkout/UseCurrentCard';
import { isToggleActive } from '../../components/featureToggle/toggles';
import moment from 'moment-timezone';
import InformationSecuredDisclaimer from '../../components/InformationSecuredDisclaimer';
import PleaseWait from '../../components/PleaseWait';
import { useEffect } from 'react';
// import moment from 'moment';

const CARD_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: 'rgba(53, 78, 121, 0.5)',
      color: '#354E79',
      fontWeight: 300,
      fontFamily: 'Poppins, Open Sans, Segoe UI, sans-serif',
      fontSize: '14px',
      fontSmoothing: 'antialiased',
      border: '1px solid rgba(0, 0, 0, 0.1);',
      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: 'rgba(53, 78, 121, 1)',
      },
    },
    invalid: {
      iconColor: 'rgba(255, 15, 0, 1)',
      color: 'rgba(255, 15, 0, 1)',
    },
  },
};

const ErrorMessage = ({ children }) => (
  <Box className="ErrorMessage" role="alert" mt={3}>
    <svg width="16" height="16" viewBox="0 0 17 17">
      <path fill="#FFF" d="M8.5,17 C3.80557963,17 0,13.1944204 0,8.5 C0,3.80557963 3.80557963,0 8.5,0 C13.1944204,0 17,3.80557963 17,8.5 C17,13.1944204 13.1944204,17 8.5,17 Z" />
      <path
        fill="#6772e5"
        d="M8.5,7.29791847 L6.12604076,4.92395924 C5.79409512,4.59201359 5.25590488,4.59201359 4.92395924,4.92395924 C4.59201359,5.25590488 4.59201359,5.79409512 4.92395924,6.12604076 L7.29791847,8.5 L4.92395924,10.8739592 C4.59201359,11.2059049 4.59201359,11.7440951 4.92395924,12.0760408 C5.25590488,12.4079864 5.79409512,12.4079864 6.12604076,12.0760408 L8.5,9.70208153 L10.8739592,12.0760408 C11.2059049,12.4079864 11.7440951,12.4079864 12.0760408,12.0760408 C12.4079864,11.7440951 12.4079864,11.2059049 12.0760408,10.8739592 L9.70208153,8.5 L12.0760408,6.12604076 C12.4079864,5.79409512 12.4079864,5.25590488 12.0760408,4.92395924 C11.7440951,4.59201359 11.2059049,4.59201359 10.8739592,4.92395924 L8.5,7.29791847 L8.5,7.29791847 Z"
      />
    </svg>
    {children}
  </Box>
);

const CheckoutFormBridge = ({
  client,
  session,
  addons,
  selectedAppointment,
  bridgeServiceEligibility,
  toastManager,
  consents,
  rescheduleAppointmentId,
  membershipType,
  type,
  onCompleted = () => {},
}) => {
  const [error, setError] = useState(null);
  const [addNewCard, setAddNewCard] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    setProcessing(true);
    event.preventDefault();

    let hasPast = false;
    if (isToggleActive('USE_FOLLOW_UP_15')) {
      try {
        const history = await this.props.client.query({
          query: APPOINTMENTS_HISTORY,
        });
        const correctedProviderId = selectedAppointment.slot.providerId.toString() === '11' ? '10' : selectedAppointment.slot.providerId.toString();

        if (history.data && history.data.getPatientAppointmentsHistory) {
          hasPast =
            history.data.getPatientAppointmentsHistory.filter(
              (x) => x.status && (x.status === '4' || x.status === '3') && x.provider && (x.provider.id.toString() === '11' ? '10' : x.provider.id.toString()) === correctedProviderId.toString()
            ).length > 0;
        }
      } catch {}
    }

    let duration = 30;

    if (isToggleActive('USE_FOLLOW_UP_15')) {
      duration = hasPast ? 15 : 30;
    }

    var dateParts = selectedAppointment.slot.date.split('/');

    var st = dateParts[2] + '-' + dateParts[0] + '-' + dateParts[1];
    const date2 = moment.tz(`${st} ${selectedAppointment.slot.startTime}`, session.department.timeZoneName);

    // todo FD > The Add x minutes should be based on the appt duration
    const date3 = moment.tz(`${st} ${selectedAppointment.slot.startTime}`, session.department.timeZoneName).add(duration, 'minutes');

    const startDateUTC = date2.utc();
    const endDateUTC = date3.utc();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      setProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const src = await stripe.createSource(cardElement, { type: 'card' });

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (error) {
      toastManager.add(`${error && error.message ? `${error.message}` : 'An error occured with the payment method. '}`, { appearance: 'error' });
      console.log('[error]', error);
    } else {
      await client.mutate({
        mutation: CREATE_PAYMENT_INTENT_BRIDGE,
        variables: {
          addons,
          membershipType,
          type, //weightloss, hypo, meno
          pm: paymentMethod.id,
          sourceId: src.source.id,
          id: selectedAppointment.slot.id.toString(),
          serviceEligibilityId: bridgeServiceEligibility.id,
          rescheduleAppointmentId,
          consents: consents
            ? consents.map((x) => {
                return {
                  type: x.type,
                  version: x.version,
                };
              })
            : null,
          appointmentDetails: {
            startDate: startDateUTC.format('YYYY-MM-DD HH:mm'),
            endDate: endDateUTC.format('YYYY-MM-DD HH:mm'),
            subject: `Paloma ${session.address ? session.address.state + ' ' : ''} patient`,
            htmlMessage: '',
            inviteSubject: `Paloma ${session.address ? session.address.state + ' ' : ''} patient`,
            inviteDescription: '',
            inviteLocation: `<a href="https://app.palomahealth.com/visit-room/${selectedAppointment.slot.providerId}">${
              (selectedAppointment.doctor || '').zocdocVideoLink.indexOf('whereby.com') < 0 ? 'ZocDoc' : 'Whereby'
            } ${session.address ? session.address.state + ' ' : ''} ${session.department.timeZoneName}</a>`,
            providerId: selectedAppointment.slot.providerId,
            departmentId: parseInt(session.department.departmentId, 10),
            startDateLocal: `${selectedAppointment.slot.date}`,
          },
        },
        refetchQueries: [{ query: CURRENT_USER }, { query: GET_BRIDGE_CONSENTS_TO_AGREE }, { query: UPCOMING_APPOINTMENTS }, { query: APPOINTMENTS }, { query: GET_MEMBERSHIPS }],
        awaitRefetchQueries: true,
      });
      setProcessing(false);
      onCompleted();
    }
  };

  return (
    <>
      <Box mr={[0, 0, 4]} mb={4} style={{ position: 'relative' }}>
        <Card p={4}>
          <Flex flexDirection="column" mb={4}>
            <Heading textAlign="center" style={{ fontWeight: 'bold' }} mb={2}>
              Payment Method
            </Heading>
            <Text textAlign="center" fontWeight={300}>
              We accept HSA and FSA cards
              <br />
              Entering payment information holds your spot.
            </Text>
          </Flex>
          {/* {!processing && ( */}
          <form onSubmit={handleSubmit}>
            {/* <Box ml="-12px" mr="-12px" mb={4} style={{ opacity: !!processing ? 0 : 1, height: !!processing ? 0 : '', maxHeight: !!processing ? 0 : '' }}> */}
            <Box>
              <Flex flexDirection="column" bg="white" p="12px">
                <Flex flexDirection="row" pb={3}>
                  <Text fontSize="16px" fontWeight="600">
                    Payment Method
                  </Text>

                  {/* {session.card &&
                    session.card.last4 &&
                    session.card.pm && (
                      <>
                        {!addNewCard ? (
                          <Text
                            fontSize="16px"
                            fontWeight="600"
                            style={{
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              color: 'rgba(246, 162, 147, 1)',
                            }}
                            onClick={() => setAddNewCard(true)}
                          >
                            + Add Card
                          </Text>
                        ) : (
                          <Text
                            fontSize="16px"
                            fontWeight="600"
                            style={{
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              color: 'rgba(246, 162, 147, 1)',
                            }}
                            onClick={() => setAddNewCard(false)}
                          >
                            Cancel
                          </Text>
                        )}
                      </>
                    )} */}
                </Flex>
                <Box
                  style={{
                    border: '1px solid rgba(0,0,0,0.1)',
                    padding: '10px 12px',
                    borderRadius: '5px',
                  }}
                >
                  {/* {session.card && session.card.last4 && session.card.pm && !addNewCard ? (
                    <UseCurrentCard />
                  ) : ( */}
                  <CardElement
                    options={CARD_OPTIONS}
                    onChange={(e) => {
                      // console.log('complete', e.complete);
                      setError(e.error);
                      setCardComplete(e.complete);
                    }}
                  />
                  {/* )} */}
                </Box>
                {error && <ErrorMessage>{error.message}</ErrorMessage>}
              </Flex>
            </Box>

            <Button
              variant="primary"
              type="submit"
              disabled={!stripe || ((!session.card || !session.card.last4 || !session.card.pm || session.addNewCard) && !cardComplete) || processing}
              mt={3}
              flex={1}
              mb={3}
              style={{ width: '100%' }}
            >
              Confirm and Pay
            </Button>
          </form>
        </Card>
        {!!processing && (
          <Flex style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: '100%', width: '100%', background: 'white' }} p={3} justifyContent={'center'} alignItems={'center'}>
            <PleaseWait text="Thank you! Hold tight as we are booking your appointment. It might take a minute or two" />
          </Flex>
        )}
      </Box>
      <InformationSecuredDisclaimer />
      {/* )} */}
    </>
  );
};

export const CheckoutFormBridgeNew = withApollo(
  withSession(
    withToastManager(({ client, session, addons, selectedAppointment, bridgeServiceEligibility, toastManager, consents, rescheduleAppointmentId, membershipType, type, onCompleted = () => {} }) => {
      const [error, setError] = useState(null);
      const [addNewCard, setAddNewCard] = useState(false);
      const [processing, setProcessing] = useState(false);
      const [cardComplete, setCardComplete] = useState(false);
      const [defaulCard, setDefaultCard] = useState(null);
      const [loadedOnFileCard, setLoadedOnFileCard] = useState(false);
      const stripe = useStripe();
      const elements = useElements();

      const handleSubmit = async (event) => {
        setProcessing(true);
        event.preventDefault();

        let hasPast = false;
        if (isToggleActive('USE_FOLLOW_UP_15')) {
          try {
            const history = await this.props.client.query({
              query: APPOINTMENTS_HISTORY,
            });
            const correctedProviderId = selectedAppointment.slot.providerId.toString() === '11' ? '10' : selectedAppointment.slot.providerId.toString();

            if (history.data && history.data.getPatientAppointmentsHistory) {
              hasPast =
                history.data.getPatientAppointmentsHistory.filter(
                  (x) => x.status && (x.status === '4' || x.status === '3') && x.provider && (x.provider.id.toString() === '11' ? '10' : x.provider.id.toString()) === correctedProviderId.toString()
                ).length > 0;
            }
          } catch {}
        }

        let duration = 30;

        if (isToggleActive('USE_FOLLOW_UP_15')) {
          duration = hasPast ? 15 : 30;
        }

        var dateParts = selectedAppointment.slot.date.split('/');

        var st = dateParts[2] + '-' + dateParts[0] + '-' + dateParts[1];
        const date2 = moment.tz(`${st} ${selectedAppointment.slot.startTime}`, session.department.timeZoneName);

        // todo FD > The Add x minutes should be based on the appt duration
        const date3 = moment.tz(`${st} ${selectedAppointment.slot.startTime}`, session.department.timeZoneName).add(duration, 'minutes');

        const startDateUTC = date2.utc();
        const endDateUTC = date3.utc();

        let paymentInfo = {};
        if (!!defaulCard && !addNewCard) {
          paymentInfo = {
            pm: defaulCard.id,
          };
        } else if (!!addNewCard) {
          if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            setProcessing(false);
            return;
          }

          const cardElement = elements.getElement(CardElement);

          const src = await stripe.createSource(cardElement, { type: 'card' });

          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',

            card: cardElement,
          });
          if (error) {
            toastManager.add(`${error && error.message ? `${error.message}` : 'An error occured with the payment method. '}`, { appearance: 'error' });
            console.log('[error]', error);
            setProcessing(false);
            return;
          } else {
            paymentInfo = {
              pm: paymentMethod.id,
              sourceId: src.source.id,
            };
          }
        }

        const success = await client.mutate({
          mutation: CREATE_PAYMENT_INTENT_BRIDGE,
          variables: {
            addons,
            membershipType,
            type, //weightloss, hypo, meno
            // pm: paymentMethod.id,
            // sourceId: src.source.id,
            ...paymentInfo,
            id: selectedAppointment.slot.id.toString(),
            serviceEligibilityId: bridgeServiceEligibility.id,
            rescheduleAppointmentId,
            consents: consents
              ? consents.map((x) => {
                  return {
                    type: x.type,
                    version: x.version,
                  };
                })
              : null,
            appointmentDetails: {
              startDate: startDateUTC.format('YYYY-MM-DD HH:mm'),
              endDate: endDateUTC.format('YYYY-MM-DD HH:mm'),
              subject: `Paloma ${session.address ? session.address.state + ' ' : ''} patient`,
              htmlMessage: '',
              inviteSubject: `Paloma ${session.address ? session.address.state + ' ' : ''} patient`,
              inviteDescription: '',
              inviteLocation: `<a href="https://app.palomahealth.com/visit-room/${selectedAppointment.slot.providerId}">${
                (selectedAppointment.doctor || '').zocdocVideoLink.indexOf('whereby.com') < 0 ? 'ZocDoc' : 'Whereby'
              } ${session.address ? session.address.state + ' ' : ''} ${session.department.timeZoneName}</a>`,
              providerId: selectedAppointment.slot.providerId,
              departmentId: parseInt(session.department.departmentId, 10),
              startDateLocal: `${selectedAppointment.slot.date}`,
            },
          },
          refetchQueries: [{ query: CURRENT_USER }, { query: GET_BRIDGE_CONSENTS_TO_AGREE }, { query: UPCOMING_APPOINTMENTS }, { query: APPOINTMENTS }, { query: GET_MEMBERSHIPS }],
          awaitRefetchQueries: true,
        });
        setProcessing(false);
        if (!!success && !!success.data && !!success.data.createPaymentIntentBridge) {
          if (!!success.data.createPaymentIntentBridge.ok) {
            onCompleted();
          } else {
            toastManager.add(`${success.data.createPaymentIntentBridge.error || 'An error occured'}`, { appearance: 'error', autoDismissTimeout:10000 });
          }
        } else {
          toastManager.add(`${'An error occured'}`, { appearance: 'error' });
        }
      };

      useEffect(() => {
        const loadDefaultCard = async () => {
          const def = await client.query({
            query: GET_STRIPE_DEFAULT_PAYMENT_METHOD_CARD,
            fetchPolicy: 'network-only',
          });
          if (def && def.data && def.data.getStripeDefaultPaymentMethodCard) {
            setDefaultCard(def.data.getStripeDefaultPaymentMethodCard);
          } else {
            setAddNewCard(true);
          }
          setLoadedOnFileCard(true);
        };

        loadDefaultCard();
      }, []);

      if (!loadedOnFileCard) {
        return (
          <Box mb={4} style={{ position: 'relative' }}>
            <Card p={'14px 20px'}>
              <Flex flexDirection="row" style={{ justifyContent: 'space-between' }}>
                <Heading style={{ fontWeight: 'bold', fontSize: '18px' }}>Payment Method</Heading>
              </Flex>
              <PleaseWait />
            </Card>
          </Box>
        );
      }
      const checkCanContinue = () => {
        if (!!addNewCard && !cardComplete) return false;
        if (!!addNewCard && !!cardComplete) return true;
        if (!!defaulCard) return true;
        return false;
      };
      return (
        <>
          <Box mb={4} style={{ position: 'relative' }}>
            <Card p={'14px 20px'}>
              <Flex flexDirection="row" style={{ justifyContent: 'space-between' }}>
                <Heading style={{ fontWeight: 'bold', fontSize: '18px' }}>Payment Method</Heading>
                {!!defaulCard && (
                  <>
                    {!addNewCard ? (
                      <Text
                        fontSize="16px"
                        fontWeight="600"
                        style={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          color: 'rgba(246, 162, 147, 1)',
                        }}
                        onClick={() => setAddNewCard(true)}
                      >
                        + Add Card
                      </Text>
                    ) : (
                      <Text
                        fontSize="16px"
                        fontWeight="600"
                        style={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          color: 'rgba(246, 162, 147, 1)',
                        }}
                        onClick={() => setAddNewCard(false)}
                      >
                        Cancel
                      </Text>
                    )}
                  </>
                )}
              </Flex>
              {/* {!processing && ( */}
              <form onSubmit={handleSubmit}>
                {/* <Box ml="-12px" mr="-12px" mb={4} style={{ opacity: !!processing ? 0 : 1, height: !!processing ? 0 : '', maxHeight: !!processing ? 0 : '' }}> */}
                <Box>
                  <Flex flexDirection="column" bg="white" p="12px">
                    <Flex flexDirection="row" pb={3} />
                    <Box
                      style={{
                        border: '1px solid rgba(0,0,0,0.1)',
                        padding: '10px 12px',
                        borderRadius: '5px',
                      }}
                    >
                      {defaulCard && defaulCard.last4 && defaulCard.id && !addNewCard ? (
                        <UseCurrentDefaultPaymentCard card={defaulCard} />
                      ) : (
                        <CardElement
                          options={CARD_OPTIONS}
                          onChange={(e) => {
                            // console.log('complete', e.complete);
                            setError(e.error);
                            setCardComplete(e.complete);
                          }}
                        />
                      )}
                    </Box>
                    {error && <ErrorMessage>{error.message}</ErrorMessage>}
                  </Flex>
                </Box>

                <Flex alignItems={'center'} justifyContent={'center'}>
                  <Button variant="primary" type="submit" disabled={!stripe || !checkCanContinue() || processing} mt={3} mb={3} style={{ width: 'fit-content' }}>
                    Confirm and Pay
                  </Button>
                </Flex>
              </form>
            </Card>
            {!!processing && (
              <Flex style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: '100%', width: '100%', background: 'white' }} p={3} justifyContent={'center'} alignItems={'center'}>
                <PleaseWait text="Thank you! Hold tight as we are booking your appointment. It might take a minute or two" />
              </Flex>
            )}
          </Box>
          {/* )} */}
        </>
      );
    })
  )
);

export default withApollo(withSession(withToastManager(CheckoutFormBridge)));
